import { ContactSelect, DateInputLocalized } from '@components'

import { Button, Flex, FormItemsGroup, Input, Spacing } from '@billy/design-system'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ImportedInvoiceReviewFormSchema } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'

import { useFormContext, useWatch } from '../../../../../../../../hooks/useForm'

interface ImportedInvoiceReviewGeneralFieldsProps {
  isClientToCreate: boolean
  isReadOnly: boolean
}

export const ImportedInvoiceReviewGeneralFields = ({
  isClientToCreate,
  isReadOnly,
}: ImportedInvoiceReviewGeneralFieldsProps): ReactElement => {
  const { t } = useTranslation()
  const { control, formState, FormItem, getValues } = useFormContext<ImportedInvoiceReviewFormSchema>()
  const [isDueDateVisible, setIsDueDateVisible] = useState(() => getValues('dueDate') !== null)
  const client = useWatch({ control, name: 'client' })

  const isDisabled = isReadOnly || formState.isSubmitting
  // Picking an existing client in its place leaves nothing to create.
  const isNewClient = isClientToCreate && !isReadOnly && !!client && !client.id

  const handleAddDueDateClick = useCallback(() => {
    setIsDueDateVisible(true)
  }, [])

  return (
    <Flex flexDirection="column" gap={Spacing.S16}>
      <FormItemsGroup itemsInRow={1}>
        <FormItem
          label={t('imported_invoices.review.form.reference_number')}
          name="referenceNumber"
          render={({ field }) => <Input {...field} disabled={isDisabled} />}
        />
        {/* No inline create: it would make a billy-api contact without the Shine customer that
            approve looks the client up in, and approve would reject it. */}
        <FormItem
          extra={isNewClient ? t('imported_invoices.review.form.client.new_hint') : undefined}
          label={t('imported_invoices.review.form.client')}
          name="client"
          render={({ field: { onChange, ref, value, ...field } }) => (
            <ContactSelect
              {...field}
              disabled={isDisabled}
              inputValue={value?.name}
              isCustomer
              onSelect={(_, contact) => onChange(contact ?? null)}
              selectedId={value?.id}
              selectedValue={value ?? undefined}
              withSearch
            />
          )}
        />
      </FormItemsGroup>

      <FormItemsGroup itemsInRow={2}>
        {/* `DateInputLocalized` and `ContactSelect` are plain function components, so they drop the
            field's `ref` rather than take one they cannot hold. `Input` forwards its own. A cleared
            date comes back `undefined`, which the form holds as `null`. */}
        <FormItem
          label={t('imported_invoices.review.form.issue_date')}
          name="issueDate"
          render={({ field: { onChange, ref, ...field } }) => (
            <DateInputLocalized {...field} disabled={isDisabled} onChange={(date) => onChange(date ?? null)} />
          )}
        />

        {isDueDateVisible && (
          <FormItem
            label={t('imported_invoices.review.form.due_date')}
            name="dueDate"
            render={({ field: { onChange, ref, ...field } }) => (
              <DateInputLocalized {...field} disabled={isDisabled} onChange={(date) => onChange(date ?? null)} />
            )}
          />
        )}

        {!isDueDateVisible && !isReadOnly && (
          /* The button takes the grid cell a `FormItem` would, so it has to align with the input
             next to it rather than with that input's label. */
          <Flex alignItems="flex-end" justifyContent="flex-start">
            <Button disabled={isDisabled} icon="plus" onClick={handleAddDueDateClick} variant="text">
              {t('imported_invoices.review.form.add_due_date')}
            </Button>
          </Flex>
        )}
      </FormItemsGroup>
    </Flex>
  )
}
